/**
 * Transport + zod codec for the wizard's step-1 question-quality gate.
 *
 *   POST /api/debate/wizard/question-quality  → score the raw question
 *   POST /api/debate/wizard/question-refine   → guided rewrite + re-score
 *
 * Unlike `wizardClient.ts` (hand-rolled casts) these payloads are parsed
 * through zod: the response drives a gate that can block the operator from
 * launching a debate, so a malformed body must fail loudly instead of
 * rendering `undefined` chips.
 */
import { z } from "zod";

import { withCsrfHeader } from "./csrf";
import type { ErrorEnvelope } from "./wizardClient";
import { WizardApiError, envelopeCode, envelopeMessage } from "./wizardClient";

const QUESTION_QUALITY_ENDPOINT = "/api/debate/wizard/question-quality";
const QUESTION_REFINE_ENDPOINT = "/api/debate/wizard/question-refine";

/* ---------------------------------------------------------------------- *
 * Schemas — mirror `macdm/orchestrator/question_quality.py` wire models.
 * ---------------------------------------------------------------------- */

export const QualityDimensionIdSchema = z.enum([
  "specificity",
  "scope",
  "measurability",
  "context",
  "decidability",
]);

export const QualityVerdictSchema = z.enum(["ready", "improvable", "vague"]);

// NOTE: no `.default()` anywhere below. Pydantic's `model_dump()` always
// emits every field (defaults included), so the wire contract is total —
// and a zod default would make the schema's INPUT type diverge from its
// OUTPUT type, which breaks the shared `postJson` generic.
const QualityOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  detail: z.string(),
});

const QualityQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  why: z.string(),
  dimension: QualityDimensionIdSchema,
  options: z.array(QualityOptionSchema),
  allowMultiple: z.boolean(),
  allowOther: z.boolean(),
});

const QualityDimensionSchema = z.object({
  id: QualityDimensionIdSchema,
  label: z.string(),
  hint: z.string(),
  weight: z.number(),
  score: z.number(),
  assessorScore: z.number(),
  verifierScore: z.number(),
  comment: z.string(),
});

const PipelineProbeSchema = z.object({
  score: z.number(),
  questionType: z.string(),
  deliverableType: z.string(),
  requirementCount: z.number(),
  entityCount: z.number(),
  entities: z.array(z.string()),
  constraintKeys: z.array(z.string()),
  charCount: z.number(),
  tautologicalRequirement: z.boolean(),
  clarificationGateSilent: z.boolean(),
});

export const QuestionQualityReportSchema = z.object({
  score: z.number(),
  verdict: QualityVerdictSchema,
  summary: z.string(),
  dimensions: z.array(QualityDimensionSchema),
  missing: z.array(z.string()),
  questions: z.array(QualityQuestionSchema),
  pipeline: PipelineProbeSchema,
  agreement: z.number(),
  llmScore: z.number(),
  elapsedMs: z.number(),
});

export const RefinedQuestionSchema = z.object({
  refined: z.string(),
  changes: z.array(z.string()),
  assumptions: z.array(z.string()),
  report: QuestionQualityReportSchema,
});

export type QualityDimensionId = z.infer<typeof QualityDimensionIdSchema>;
export type QualityVerdict = z.infer<typeof QualityVerdictSchema>;
export type QualityOption = z.infer<typeof QualityOptionSchema>;
export type QualityQuestion = z.infer<typeof QualityQuestionSchema>;
export type QualityDimension = z.infer<typeof QualityDimensionSchema>;
export type PipelineProbe = z.infer<typeof PipelineProbeSchema>;
export type QuestionQualityReport = z.infer<typeof QuestionQualityReportSchema>;
export type RefinedQuestion = z.infer<typeof RefinedQuestionSchema>;

/** One answered clarifying question, ready for the refine endpoint. */
export interface QuestionRefineAnswer {
  questionId: string;
  question: string;
  /** Chosen option LABELS (the backend reasons over text, not ids). */
  selected: string[];
  /** Free-text "Diğer" input. */
  other: string;
}

/* ---------------------------------------------------------------------- *
 * Transport
 * ---------------------------------------------------------------------- */

async function readJsonSafe(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

async function postJson<T>(
  endpoint: string,
  body: unknown,
  schema: z.ZodType<T>,
  fallbackMessage: string,
  signal?: AbortSignal,
): Promise<T> {
  const init: RequestInit = {
    method: "POST",
    headers: withCsrfHeader({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(body),
    cache: "no-store",
    credentials: "include",
  };
  if (signal) init.signal = signal;

  const response = await fetch(endpoint, init);
  if (!response.ok) {
    const payload = (await readJsonSafe(response)) as ErrorEnvelope | null;
    throw new WizardApiError(
      envelopeMessage(payload) ?? `${fallbackMessage} (HTTP ${response.status})`,
      response.status,
      envelopeCode(payload),
    );
  }

  const parsed = schema.safeParse(await readJsonSafe(response));
  if (!parsed.success) {
    console.warn("[questionQuality] rejected payload", parsed.error.issues.slice(0, 3));
    throw new WizardApiError(
      "Soru değerlendirici beklenmeyen bir yanıt döndürdü.",
      response.status,
      "wizard.question_quality.invalid_payload",
    );
  }
  return parsed.data;
}

export async function assessQuestionQuality(
  query: string,
  options: { signal?: AbortSignal } = {},
): Promise<QuestionQualityReport> {
  return postJson(
    QUESTION_QUALITY_ENDPOINT,
    { query },
    QuestionQualityReportSchema,
    "Soru değerlendirici yanıt vermedi",
    options.signal,
  );
}

export async function refineQuestion(
  query: string,
  answers: QuestionRefineAnswer[],
  options: { signal?: AbortSignal } = {},
): Promise<RefinedQuestion> {
  return postJson(
    QUESTION_REFINE_ENDPOINT,
    { query, answers },
    RefinedQuestionSchema,
    "Soru yeniden yazıcı yanıt vermedi",
    options.signal,
  );
}
